import React from 'react';
import {PageContainer} from '@ant-design/pro-components';
import {Button, Space, Tabs, Typography} from 'antd';
import {PlayCircleOutlined, ReloadOutlined, BarChartOutlined} from '@ant-design/icons';
import {useAtom} from 'jotai';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {useTriggerScrape} from './api';
import {activeTabAtom, scrapingLoadingAtom} from './atoms';
import ContentTab from './components/ContentTab';
import CreatorsTab from './components/CreatorsTab';
import TopicsTab from './components/TopicsTab';
import FilterSettingsTab from './components/FilterSettingsTab';
import EnrichModal from './components/EnrichModal';
import CacheTab from './CacheTab';
import ScrapingTab from './ScrapingTab';
import { Link } from 'react-router-dom';

const {Text} = Typography;

// Create a query client instance
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: 1,
        },
    },
});

const InstagramInfluencersDashboardContent: React.FC = () => {
    const [activeTab, setActiveTab] = useAtom(activeTabAtom);
    const [scrapingLoading, setScrapingLoading] = useAtom(scrapingLoadingAtom);
    const triggerScrapeMutation = useTriggerScrape();

    const handleScrape = async () => {
        setScrapingLoading(true);
        try {
            await triggerScrapeMutation.mutateAsync();
        } finally {
            setScrapingLoading(false);
        }
    };

    const handleRefresh = () => {
        queryClient.invalidateQueries();
    };

    const tabItems = [
        {
            key: 'content',
            label: 'Content',
            children: <ContentTab/>,
        },
        {
            key: 'creators',
            label: 'Tracked Creators',
            children: <CreatorsTab/>,
        },
        {
            key: 'topics',
            label: 'Topics',
            children: <TopicsTab/>,
        },
        {
            key: 'settings',
            label: 'Filter Settings',
            children: <FilterSettingsTab/>,
        },
        {
            key: 'scraping',
            label: 'Scraping Control',
            children: <ScrapingTab/>,
        },
        // {
        //     key: 'cache',
        //     label: 'Cache Management',
        //     children: <CacheTab/>,
        // },
    ];

    return (
        <PageContainer
            header={{
                title: 'Instagram Influencers Dashboard',
                subTitle: 'Manage tracked creators and enrich content',
                extra: (
                    <Space>
                        <Link to="/reporting/posts-metrics">
                            <Button icon={<BarChartOutlined />}>
                                Post Metrics
                            </Button>
                        </Link>
                        <Button
                            icon={<ReloadOutlined/>}
                            onClick={handleRefresh}
                            title="Refresh all data"
                        >
                            Refresh
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlayCircleOutlined/>}
                            onClick={handleScrape}
                            loading={scrapingLoading || triggerScrapeMutation.isLoading}
                            title="Start content scraping"
                        >
                            Start Scraping
                        </Button>
                    </Space>
                ),
            }}
        >
            <div style={{marginBottom: 16}}>
                <Text type="secondary">
                    Monitor Instagram influencer content, manage tracked creators, and enrich content with AI analysis.
                </Text>
            </div>

            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
                size="large"
                style={{marginTop: 16}}
            />

            <EnrichModal/>
        </PageContainer>
    );
};

const InstagramInfluencersDashboard: React.FC = () => {
    return (
        <QueryClientProvider client={queryClient}>
            <InstagramInfluencersDashboardContent/>
        </QueryClientProvider>
    );
};

export default InstagramInfluencersDashboard;